import { unzipSync } from 'fflate';

import { parseKml } from './fr24-kml';
import type { FormatParser, ParsedTrack } from './types';
import { ParseError } from './types';

/** Zip local file header: `PK` then 0x03 0x04. */
const ZIP_MAGIC = `PK${String.fromCharCode(3, 4)}`;

/**
 * KMZ: a zip with a KML inside it.
 *
 * `doc.kml` is the convention and what Google Earth writes, but an archive
 * whose KML is named after the flight is common enough that falling back to the
 * first `.kml` entry is the more useful rule. Both, in that order.
 */
export function parseKmz(bytes: Uint8Array): ParsedTrack {
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes);
  } catch (cause) {
    throw new ParseError('This .kmz file could not be unzipped — it may be truncated.', [
      cause instanceof Error ? cause.message : String(cause),
    ]);
  }

  const names = Object.keys(files);
  const preferred =
    names.find((name) => name.toLowerCase() === 'doc.kml') ??
    names.find((name) => name.toLowerCase().endsWith('.kml'));
  const contents = preferred === undefined ? undefined : files[preferred];

  if (preferred === undefined || contents === undefined) {
    throw new ParseError('This .kmz archive contains no .kml file.', [
      `Archive contains: ${names.join(', ')}`,
    ]);
  }

  const parsed = parseKml(new TextDecoder().decode(contents));
  return { ...parsed, notes: [`Unzipped ${preferred}`, ...parsed.notes] };
}

export const kmzParser: FormatParser = {
  format: 'kmz',
  label: 'KMZ (zipped KML)',
  extensions: ['.kmz'],
  /*
   * Sniffing a binary container through a UTF-8 decode is unreliable, so KMZ
   * detection leans on the extension and this only confirms the magic bytes.
   */
  sniff: (text) => text.startsWith(ZIP_MAGIC),
  parse: parseKmz,
};
